import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { UsageStats } from "@/components/dashboard/UsageStats";
import { BillingChart } from "@/components/dashboard/BillingChart";
import { ModuleTabs } from "@/components/dashboard/ModuleTabs";
import { OpenReportsBanner } from "@/components/dashboard/OpenReportsBanner";
import {
  Users, KeyRound, UserCog, LogIn, Search as SearchIcon, Eye, ShieldAlert,
  Loader2, ArrowRight, Clock, Activity, FilePlus, FilePenLine, FileX, LogOut, FileUp, Building2, MapPin, Ban, AlertTriangle,
} from "lucide-react";


const ACTION_ICON = {
  connexion: LogIn, deconnexion: LogOut, recherche: SearchIcon, consultation: Eye,
  creation_societe: Building2, modification_societe: FilePenLine, suppression_societe: FileX,
  creation_site: FilePlus, modification_site: FilePenLine, suppression_site: FileX,
  import_sites: FileUp,
};

function fmt(ts) {
  try { return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}
function fmtMissionWhen(m) {
  const p = (m.date || "").split("-");
  const day = p.length === 3 ? `${p[2]}/${p[1]}` : (m.date || "");
  return `${day} à ${m.start_time || ""}${m.end_time ? "–" + m.end_time : ""}`;
}

function StatCard({ icon: Icon, label, value, accent, onClick, testid, delay }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className="text-left border border-white/10 rounded-lg p-5 bg-[#121212] hover:border-[#007AFF]/40 hover:bg-zinc-900/60 transition-colors fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <Icon className={`w-5 h-5 mb-3 ${accent || "text-[#007AFF]"}`} />
      <p className="font-display text-3xl font-semibold">{value}</p>
      <p className="text-zinc-500 text-sm mt-0.5">{label}</p>
    </button>
  );
}

// Cache module (stale-while-revalidate) : en revenant sur le dashboard, les données s'affichent
// instantanément depuis le cache pendant qu'un rafraîchissement a lieu en arrière-plan.
let _dashCache = null;
let _otCache = null;

export default function DashboardHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(_dashCache);
  const [ot, setOt] = useState(_otCache);

  useEffect(() => {
    const mk = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
    api.get("/gardiennage/overtime-summary", { params: { month: mk } }).then(({ data }) => { _otCache = data; setOt(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try { const { data } = await api.get("/dashboard"); if (active) { _dashCache = data; setData(data); } } catch {}
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { active = false; window.removeEventListener("focus", onFocus); };
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 text-[#007AFF] animate-spin" /></div>;
  }

  const t = data.totals;

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-8">
        <p className="text-[#FFB020] font-code text-sm uppercase tracking-widest">Tableau de bord</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Bonjour, {user?.name?.split(" ")[0] || "Manager"}</h1>
        <p className="text-zinc-400 text-sm mt-2">Vue d'ensemble de votre parc clients et de l'activité des agents.</p>
      </div>

      {data.unfilled_missions_24h?.length > 0 && (
        <div data-testid="unfilled-missions-banner" className="mb-8 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-5 fade-up">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0" />
            <h2 className="font-display text-lg font-semibold text-[#FF3B30]" data-testid="unfilled-missions-title">
              {data.unfilled_missions_24h.length} mission{data.unfilled_missions_24h.length > 1 ? "s" : ""} non pourvue{data.unfilled_missions_24h.length > 1 ? "s" : ""} dans moins de 24h
            </h2>
          </div>
          <p className="text-zinc-400 text-sm mb-3">Aucun agent assigné — affectez un rondier avant l'heure de prise de service.</p>
          <div className="space-y-2">
            {data.unfilled_missions_24h.map((m) => (
              <button key={m.mission_id} onClick={() => navigate("/dashboard/planning")} data-testid={`unfilled-mission-${m.mission_id}`}
                className="w-full text-left flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-[#1a0e0e] border border-[#FF3B30]/25 px-4 py-2.5 hover:border-[#FF3B30]/60 hover:bg-[#FF3B30]/10 transition-colors">
                <span className="text-white font-medium">{m.societe_nom || "—"}</span>
                <span className="text-zinc-400 text-sm flex items-center gap-1"><MapPin className="w-3.5 h-3.5 shrink-0" /> {m.site_nom || "—"}</span>
                <span className="ml-auto text-[#FFB020] font-code text-sm flex items-center gap-1"><Clock className="w-3.5 h-3.5 shrink-0" /> {fmtMissionWhen(m)}</span>
                <span className="text-[11px] text-[#FF3B30] font-semibold uppercase tracking-wide border border-[#FF3B30]/40 rounded px-1.5 py-0.5">Aucun agent</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <OpenReportsBanner items={data.open_reports_overdue} />

      <ModuleTabs user={user} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testid="dash-stat-sites-actifs" icon={MapPin} label="Sites actifs" value={t.sites} accent="text-[#007AFF]" onClick={() => navigate("/dashboard/clients")} delay={0} />
        <StatCard testid="dash-stat-sites-resilies" icon={Ban} label="Sites résiliés" value={t.sites_resilies} accent={t.sites_resilies > 0 ? "text-[#FF3B30]" : "text-zinc-400"} onClick={() => navigate("/dashboard/resilies")} delay={60} />
        <StatCard testid="dash-stat-cles-circulation" icon={KeyRound} label="Clés en circulation" value={t.cles_circulation} accent={t.cles_circulation > 0 ? "text-[#FFB020]" : "text-zinc-400"} onClick={() => navigate("/dashboard/suivi-cles")} delay={120} />
        <StatCard testid="dash-stat-agents-actifs" icon={UserCog} label="Agents actifs" value={t.agents} accent="text-emerald-400" onClick={() => navigate("/dashboard/agents")} delay={180} />
        <StatCard testid="dash-stat-clients" icon={Building2} label="Clients" value={t.societes} accent="text-[#007AFF]" onClick={() => navigate("/dashboard/clients")} delay={240} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="dash-stat-societes" icon={Building2} label="Sociétés" value={t.societes} onClick={() => navigate("/dashboard/clients")} delay={0} />
        <StatCard testid="dash-stat-sites" icon={MapPin} label="Sites" value={t.sites} onClick={() => navigate("/dashboard/clients")} delay={60} />
        <StatCard testid="dash-stat-keys" icon={KeyRound} label="Clés référencées" value={t.keys} accent="text-[#FFB020]" onClick={() => navigate("/dashboard/clients")} delay={120} />
        <StatCard testid="dash-stat-alerts" icon={ShieldAlert} label="Alertes non lues" value={data.unread_alerts} accent={data.unread_alerts > 0 ? "text-[#FF3B30]" : "text-zinc-400"} onClick={() => navigate("/dashboard/suivi")} delay={180} />
      </div>

      {ot && (
        <div onClick={() => navigate("/dashboard/suivi-gardiennage")} data-testid="dash-overtime-card"
          className="cursor-pointer border border-white/10 rounded-lg p-5 bg-[#121212] hover:border-[#FF3B30]/40 transition-colors mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${ot.count > 0 ? "text-[#FF3B30]" : "text-zinc-500"}`} />
              <div>
                <p className="font-display text-2xl font-semibold" data-testid="dash-overtime-count">{ot.count}</p>
                <p className="text-zinc-500 text-sm">Dépassements gardiennage (ce mois)</p>
              </div>
            </div>
            {ot.by_site?.length > 0 && (
              <div className="text-right text-xs text-zinc-400">
                <p className="text-zinc-500 mb-1">Sites qui débordent</p>
                {ot.by_site.slice(0, 3).map((s, i) => (<p key={i}>{s.site_nom} · {s.count}×</p>))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <StatCard testid="dash-stat-connexions" icon={LogIn} label="Connexions (total)" value={t.connexions} onClick={() => navigate("/dashboard/suivi")} delay={220} />
        <StatCard testid="dash-stat-recherches" icon={SearchIcon} label="Recherches (total)" value={t.recherches} onClick={() => navigate("/dashboard/suivi")} delay={260} />
        <StatCard testid="dash-stat-consultations" icon={Eye} label="Consultations (total)" value={t.consultations} onClick={() => navigate("/dashboard/suivi")} delay={300} />
      </div>

      <UsageStats />

      {["manager", "admin", "demo"].includes(user?.role) && <BillingChart />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent alerts */}
        <div className="border border-white/10 rounded-lg bg-[#121212] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#FFB020]" /><span className="font-display font-medium">Dernières alertes</span></div>
            <button onClick={() => navigate("/dashboard/suivi")} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="dash-see-suivi">
              Voir le suivi <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-white/5" data-testid="dash-recent-alerts">
            {data.recent_alerts.length === 0 ? (
              <div className="px-5 py-10 text-center text-zinc-500 text-sm">Aucune alerte. Tout est normal.</div>
            ) : data.recent_alerts.map((a) => (
              <div key={a.log_id} className="px-5 py-3.5 flex items-start gap-3">
                <ShieldAlert className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm"><span className="font-medium">{a.user_name}</span> — {a.details_text}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{a.alert_reason}</p>
                  <p className="text-xs text-zinc-600 mt-1 font-code flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(a.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="border border-white/10 rounded-lg bg-[#121212] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2"><Activity className="w-4 h-4 text-[#007AFF]" /><span className="font-display font-medium">Activité récente</span></div>
            <button onClick={() => navigate("/dashboard/suivi")} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="dash-see-activity">
              Journal complet <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-white/5" data-testid="dash-recent-activity">
            {data.recent_activity.length === 0 ? (
              <div className="px-5 py-10 text-center text-zinc-500 text-sm">Aucune activité pour le moment.</div>
            ) : data.recent_activity.map((l) => {
              const Icon = ACTION_ICON[l.action] || Activity;
              return (
                <div key={l.log_id} className="px-5 py-3 flex items-center gap-3">
                  <Icon className="w-4 h-4 text-zinc-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate"><span className="font-medium">{l.user_name}</span> · {l.action_label}{l.details_text ? ` — ${l.details_text}` : ""}</p>
                  </div>
                  <span className="text-xs text-zinc-600 font-code shrink-0">{fmt(l.timestamp)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
